import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

// Smooth scroll to top function
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// Enhanced Link component with scroll to top
const FooterLink = ({ to, children, ...props }) => {
  const handleClick = (e) => {
    // Only scroll to top if it's not the current page
    if (window.location.pathname !== to) {
      scrollToTop();
    }
  };

  return (
    <Link to={to} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
};

const FooterStyles = () => {
  useEffect(() => {
    const css = `
      .footer-container {
        background: linear-gradient(135deg, #1a3a52 0%, #2d5a7b 50%, #1a3a52 100%);
        color: #e8f4f8;
        padding: 100px 20px 0;
        font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif;
        position: relative;
        overflow: hidden;
      }

      .footer-container::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 1px;
        background: linear-gradient(90deg, transparent 0%, #6bbe44 50%, transparent 100%);
        box-shadow: 0 0 20px rgba(107, 190, 68, 0.5);
      }

      .footer-container::after {
        content: '';
        position: absolute;
        top: -30%;
        right: -15%;
        width: 600px;
        height: 600px;
        background: radial-gradient(circle, rgba(107, 190, 68, 0.12) 0%, transparent 60%);
        border-radius: 50%;
        pointer-events: none;
        animation: float 20s ease-in-out infinite;
      }

      @keyframes float {
        0%, 100% { transform: translate(0, 0) scale(1); }
        50% { transform: translate(-50px, -50px) scale(1.1); }
      }

      .footer-grid-bg {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image: 
          linear-gradient(rgba(107, 190, 68, 0.03) 1px, transparent 1px),
          linear-gradient(90deg, rgba(107, 190, 68, 0.03) 1px, transparent 1px);
        background-size: 50px 50px;
        opacity: 0.4;
        pointer-events: none;
      }

      .footer-content {
        display: grid;
        grid-template-columns: 1.8fr 1fr 1fr 1.2fr;
        gap: 60px;
        max-width: 1400px;
        margin: 0 auto 80px;
        position: relative;
        z-index: 1;
      }

      .footer-section {
        animation: fadeInUp 0.8s ease-out backwards;
        /* NOTE: This section has no 'background: white'. 
           If you see a white card, it's from an external CSS file. */
      }

      .footer-section:nth-child(1) { animation-delay: 0.1s; }
      .footer-section:nth-child(2) { animation-delay: 0.2s; }
      .footer-section:nth-child(3) { animation-delay: 0.3s; }
      .footer-section:nth-child(4) { animation-delay: 0.4s; }

      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .footer-section h4 {
        color: #ffffff;
        font-size: 0.85rem;
        font-weight: 700;
        margin-bottom: 32px;
        position: relative;
        text-transform: uppercase;
        letter-spacing: 2.5px;
        display: inline-block;
      }

      .footer-section h4::after {
        content: '';
        display: block;
        width: 100%;
        height: 2px;
        background: linear-gradient(90deg, #6bbe44 0%, transparent 100%);
        margin-top: 14px;
        border-radius: 2px;
        box-shadow: 0 0 10px rgba(107, 190, 68, 0.4);
      }

      .footer-section.about .footer-logo {
        color: #ffffff;
        text-decoration: none;
        font-size: 2.4rem;
        font-weight: 800;
        display: flex;
        align-items: center;
        margin-bottom: 24px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        letter-spacing: -0.5px;
      }

      .footer-section.about .footer-logo:hover {
        transform: translateX(8px);
        color: #6bbe44;
      }

      .footer-section.about .footer-logo-img {
        height: 160px;
        margin-right: 16px;
        filter: drop-shadow(0 4px 12px rgba(107, 190, 68, 0.3));
        transition: transform 0.4s ease;
      }

      .footer-section.about .footer-logo:hover .footer-logo-img {
        transform: rotate(5deg) scale(1.05);
      }

      .footer-section.about p {
        font-size: 1.05rem;
        line-height: 1.9;
        margin-bottom: 10px;
        color: #94b8d0;
        font-weight: 400;
      }

      .footer-section.about .footer-tagline {
        font-size: 0.98rem;
        color: #7ea8c4;
        line-height: 1.7;
        padding: 20px;
        background: rgba(107, 190, 68, 0.08);
        border-left: 4px solid #6bbe44;
        margin-top: 28px;
        border-radius: 0 8px 8px 0;
        backdrop-filter: blur(10px);
        transition: all 0.4s ease;
      }

      .footer-section.about .footer-tagline:hover {
        background: rgba(107, 190, 68, 0.15);
        transform: translateX(5px);
        border-left-width: 6px;
      }

      .footer-section.links ul {
        list-style: none;
        padding: 0;
      }

      .footer-section.links li {
        margin-bottom: 18px;
      }

      .footer-section.links a {
        color: #94b8d0;
        text-decoration: none;
        font-size: 1.05rem;
        font-weight: 500;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        display: inline-flex;
        align-items: center;
        position: relative;
        padding: 8px 0;
      }

      .footer-section.links a::after {
        content: '→';
        position: absolute;
        right: -25px;
        opacity: 0;
        transition: all 0.3s ease;
        color: #6bbe44;
        font-weight: 700;
      }

      .footer-section.links a:hover {
        color: #6bbe44;
        transform: translateX(8px);
        letter-spacing: 0.5px;
      }

      .footer-section.links a:hover::after {
        opacity: 1;
        right: -30px;
      }

      .footer-section.contact-info .contact-item {
        margin-bottom: 18px;
        font-size: 1.02rem;
        display: flex;
        align-items: center;
        line-height: 1.6;
        transition: all 0.3s ease;
      }

      .footer-section.contact-info .contact-item:hover {
        transform: translateX(5px);
      }

      .footer-section.contact-info .contact-item i {
        margin-right: 14px;
        color: #6bbe44;
        font-size: 1.15rem;
        min-width: 24px;
        text-align: center;
        transition: all 0.3s ease;
      }

      .footer-section.contact-info .contact-item:hover i {
        transform: scale(1.15);
        color: #7fd94f;
      }

      .footer-section.contact-info .contact-text {
        color: #94b8d0;
        transition: color 0.3s ease;
      }

      .footer-section.contact-info .contact-item:hover .contact-text {
        color: #ffffff;
      }

      .footer-section.social-media .social-icons {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
      }

      .footer-section.social-media .social-icons a {
        color: #94b8d0;
        font-size: 1.3rem;
        width: 52px;
        height: 52px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 14px;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        border: 2px solid rgba(107, 190, 68, 0.2);
        position: relative;
        overflow: hidden;
      }

      .footer-section.social-media .social-icons a::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, #6bbe44 0%, #4a9d2f 100%);
        opacity: 0;
        transition: opacity 0.4s ease;
        z-index: -1;
      }

      .footer-section.social-media .social-icons a:hover {
        color: #ffffff;
        transform: translateY(-8px) rotate(5deg);
        border-color: #6bbe44;
        box-shadow: 0 12px 28px rgba(107, 190, 68, 0.4);
      }

      .footer-section.social-media .social-icons a:hover::before {
        opacity: 1;
      }

      .footer-bottom {
        text-align: center;
        padding: 40px 20px;
        border-top: 1px solid rgba(107, 190, 68, 0.15);
        font-size: 0.95rem;
        position: relative;
        z-index: 1;
        background: rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(10px);
      }

      .footer-bottom p {
        margin: 0;
        color: #7ea8c4;
        font-weight: 500;
        letter-spacing: 0.5px;
      }

      .footer-bottom p span {
        color: #6bbe44;
        font-weight: 700;
      }

      /* Responsive */
      @media (max-width: 1100px) {
        .footer-content {
          grid-template-columns: 1.5fr 1fr 1fr;
          gap: 50px;
        }

        .footer-section.social-media {
          grid-column: 1 / -1;
        }

        .footer-section.social-media h4::after {
          width: 60px;
          margin-left: auto;
          margin-right: auto;
        }

        .footer-section.social-media .social-icons {
          justify-content: center;
        }
      }

      @media (max-width: 768px) {
        .footer-container {
          padding: 80px 20px 0;
        }

        .footer-content {
          grid-template-columns: 1fr;
          gap: 50px;
        }

        .footer-section h4 {
          text-align: center;
        }

        .footer-section h4::after {
          margin-left: auto;
          margin-right: auto;
          width: 60px;
        }

        .footer-section.about {
          text-align: center;
        }

        .footer-section.about .footer-logo {
          justify-content: center;
        }

        .footer-section.about .footer-tagline {
          border-left: none;
          border-top: 4px solid #6bbe44;
          border-radius: 8px;
          padding-top: 20px;
        }

        .footer-section.links {
          text-align: center;
        }

        .footer-section.links a {
          justify-content: center;
        }

        .footer-section.contact-info .contact-item {
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
        }

        .footer-section.social-media .social-icons {
          justify-content: center;
        }
      }

      @media (max-width: 480px) {
        .footer-section.about .footer-logo {
          font-size: 2rem;
        }

        .footer-section.about .footer-logo-img {
          height: 100px;
        }

        .footer-section h4 {
          font-size: 0.8rem;
        }

        .footer-section.social-media .social-icons a {
          width: 48px;
          height: 48px;
          font-size: 1.2rem;
        }
      }
    `;

    const styleEl = document.createElement('style');
    styleEl.id = 'footer-styles';
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    return () => {
      const el = document.getElementById('footer-styles');
      if (el) {
        document.head.removeChild(el);
      }
    };
  }, []);

  return null;
};

function Footer() {
  return (
    <>
      <FooterStyles />

      <footer className="footer-container">
        <div className="footer-grid-bg"></div>
        
        <div className="footer-content">
          <div className="footer-section about">
            <FooterLink to="/" className="footer-logo">
              <img
                src="/images/footer.png"
                alt="Rafferty Logo"
                className="footer-logo-img"
              />
            </FooterLink>
            <p>For International Trade</p>
            <p className="footer-tagline">
              Exporting nature's finest herbs and botanicals worldwide, with
              integrity and sustainability at our core.
            </p>
          </div>

          <div className="footer-section links">
            <h4>Quick Links</h4>
            <ul>
              <li>
                <FooterLink to="/about">About Us</FooterLink>
              </li>
              <li>
                <FooterLink to="/products">Products</FooterLink>
              </li>
              <li>
                <FooterLink to="/contact">Contact Us</FooterLink>
              </li>
            </ul>
          </div>

          <div className="footer-section contact-info">
            <h4>Contact Info</h4>
            <div className="contact-item">
              {/* This icon will appear after you add the Font Awesome link */}
              <i className="fas fa-map-marker-alt"></i>
              <span className="contact-text">Egypt</span>
            </div>
            <div className="contact-item">
              <i className="fas fa-envelope"></i>
              <span className="contact-text">info@rafferty.com</span>
            </div>
      
          </div>

          <div className="footer-section social-media">
            <h4>Follow Us</h4>
            <div className="social-icons">
          
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <i className="fab fa-linkedin-in"></i>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            &copy; {new Date().getFullYear()} <span>Rafferty International Trade</span>. All rights reserved.
          </p>
        </div>
      </footer>
    </>
  );
}

export default Footer;